import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lwt_access_token";
const REFRESH_KEY = "lwt_refresh_token";
export const getAuthToken = () => { try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch (e) { return ""; } };
export const setAuthToken = (t) => { try { if (t) window.localStorage.setItem(TOKEN_KEY, t); else window.localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ } };
export const getRefreshToken = () => { try { return window.localStorage.getItem(REFRESH_KEY) || ""; } catch (e) { return ""; } };
export const setRefreshToken = (t) => { try { if (t) window.localStorage.setItem(REFRESH_KEY, t); else window.localStorage.removeItem(REFRESH_KEY); } catch (e) { /* ignore */ } };

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Inject Authorization header from localStorage on EVERY request.
// This is the fallback when cross-origin cookies are blocked
// (Safari/iOS, strict 3rd-party cookie policies, lawinetech.com ↔ API host).
api.interceptors.request.use((config) => {
  const tok = getAuthToken();
  if (tok && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${tok}` };
  }
  return config;
});

// Auto-append `lang` query param to GET requests on translatable public
// endpoints. The value comes from localStorage (managed by LangContext).
// Backend endpoints that accept `?lang=` will lazy-translate content on the fly
// (cached after first call). Endpoints that don't know the param simply ignore it.
const TRANSLATABLE_GET_PATTERNS = [
  /^\/news/, /^\/events?$/, /^\/events\//, /^\/companies/, /^\/categories/,
  /^\/resources/, /^\/press\/news/, /^\/press\/resources/, /^\/formations/,
  /^\/public\//,
];
api.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() === "get") {
    const url = config.url || "";
    const matches = TRANSLATABLE_GET_PATTERNS.some((re) => re.test(url));
    if (matches) {
      const lang = (typeof window !== "undefined" && window.localStorage.getItem("lwt_lang")) || "fr";
      config.params = { ...(config.params || {}), lang: config.params?.lang || lang };
    }
  }
  return config;
});

// ---- Response interceptor: auto-refresh access token on 401 "Token expired" ----
// Single-flight refresh: multiple parallel requests that hit 401 share the same
// refresh promise, then retry with the new access token. If refresh fails, we
// clear tokens and dispatch an "auth:logout" event so AuthContext can react.
let refreshPromise = null;
const doRefresh = async () => {
  if (!refreshPromise) {
    const rt = getRefreshToken();
    // Use bare axios (no interceptors) to avoid recursion on failure
    refreshPromise = axios
      .post(`${API}/auth/refresh`, rt ? { refresh_token: rt } : {}, { withCredentials: true })
      .then((r) => {
        const newTok = r.data?.access_token;
        if (newTok) setAuthToken(newTok);
        return newTok;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

const notifyLogout = () => {
  setAuthToken(null);
  setRefreshToken(null);
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  } catch (e) { /* ignore */ }
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const url = original.url || "";
    // Don't try to refresh on the refresh/login/logout endpoints themselves
    const isAuthEndpoint = /\/auth\/(refresh|login|register|logout)/.test(url);
    const isExpired = status === 401 && (detail === "Token expired" || detail === "Not authenticated");

    if (isExpired && !isAuthEndpoint && !original._retry) {
      original._retry = true;
      try {
        const newTok = await doRefresh();
        if (newTok) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newTok}` };
          return api(original);
        }
      } catch (e) { /* refresh failed */ }
      notifyLogout();
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
